import React, { useContext } from 'react';
import { Accordion, AccordionDetails, AccordionSummary } from '@mui/material';
import Stack from '../../../../../atomicDesign/atoms/Stack';
import Box from '../../../../../atomicDesign/atoms/Box';
import Text from '../../../../../atomicDesign/atoms/Text';
import SimpleTable from '../../../../../atomicDesign/molecules/SimpleTable';
import DataRow from '../../../../../atomicDesign/atoms/DataRow';
import DataCell from '../../../../../atomicDesign/atoms/DataCell';

import DaySummary from './dailySummary';
import type { UiWeek } from './types';
import { useDataExporter } from '../../../../../customhooks/useExporter';
import IconOnlyButton from '../../../../../atomicDesign/molecules/Buttons/IconOnlyButton';
import AdminContext from '../../../../../context/admin';
import MuiFaIcon from '../../../../../atomicDesign/atoms/MuiFaIcon';

type AdminContextType = {
  language?: {
    eosDashboard?: { noDataAvailable?: string };
    settings?: {
      baseline?: {
        weeklySummary?: string;
        week?: string;
        summary?: string;
        energyTypes?: { [key: string]: string };
      };
    };
  };
};

export default function WeeklySummarySection({
  weeks,
  buildingName,
}: {
  weeks: Array<[string, UiWeek]>;
  buildingName: string | undefined;
}) {
  const { language } = useContext(AdminContext) as AdminContextType;

  const { handleExport } = useDataExporter() as {
    handleExport: (
      type: 'excel' | 'csv',
      options: any,
      setFeedback?: any
    ) => void;
  };

  const sortedWeeks = React.useMemo(
    () => weeks.slice().sort(([a], [b]) => Number(a) - Number(b)),
    [weeks]
  );

  const exportWeekly = (type: 'excel' | 'csv') => {
    const rows = sortedWeeks.flatMap(([weekKey, week]) =>
      Object.entries(week.dayOfWeek ?? {}).flatMap(([dayKey, day]) =>
        (day.categorized ?? []).map((c) => ({
          week: week.weekNumber ?? weekKey,
          day: dayKey,
          typeOfEnergy:
            language?.settings?.baseline?.energyTypes?.[c.typeOfEnergy] ??
            c.typeOfEnergy,
          avg: c.avg,
          total: c.total,
          kWp: c.kWp,
          totalCo2: c.totalCo2,
          numberOfDevices: c.breakdown?.length ?? 0,
        }))
      )
    );

    handleExport(type, {
      data: rows,
      filename: `baseline_weekly_${buildingName ?? 'building'}`,
      sheetName: 'Weekly',
      headers: [
        'week',
        'day',
        'year',
        'month',
        'typeOfEnergy',
        'avg',
        'total',
        'kWp',
        'co2Total',
        'numberOfDevices',
        'excluded',
      ],
    });
  };

  const renderWeekDays = (week: UiWeek) => {
    const days = Object.entries(week.dayOfWeek ?? {}).sort(
      ([a], [b]) => Number(a) - Number(b)
    );

    if (days.length === 0) {
      return (
        <Text variant="body2">
          {language?.eosDashboard?.noDataAvailable ?? 'No data.'}
        </Text>
      );
    }

    return (
      <Stack spacing={1}>
        {days.map(([dayKey, day]) => (
          <DaySummary key={dayKey} dayKey={dayKey} day={day} />
        ))}
      </Stack>
    );
  };

  return (
    <Accordion
      slotProps={{
        transition: { timeout: 0 },
      }}
    >
      <AccordionSummary
        sx={{ typography: 'subtitle1' }}
        expandIcon={<MuiFaIcon name="fa-solid fa-chevron-down" />}
      >
        <Stack direction="row" sx={{ alignItems: 'center', gap: 2.5 }}>
          <Text>
            {language?.settings?.baseline?.weeklySummary ?? 'Weekly summary'}
          </Text>

          <IconOnlyButton
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              exportWeekly('excel');
            }}
            onMouseDown={(e: React.MouseEvent) => e.stopPropagation()}
            icon={
              <MuiFaIcon
                name="fa-regular fa-download"
                style={{ fontSize: 14 }}
              />
            }
            disabled={!weeks.length}
          />
        </Stack>
      </AccordionSummary>

      <AccordionDetails>
        <Box>
          <SimpleTable
            columns={[
              {
                label: language?.settings?.baseline?.week ?? 'Week',
                width: '80px',
                align: 'left',
              },
              {
                label: language?.settings?.baseline?.summary ?? 'Summary',
                align: 'left',
              },
            ]}
            sx={{ mb: 0 }}
          >
            {sortedWeeks.map(([weekKey, week]) => {
              const weekLabel = week.weekNumber ?? weekKey;

              return (
                <DataRow
                  key={weekKey}
                  sx={{
                    '&:hover': { backgroundColor: 'rgba(0,0,0,0.03)' },
                  }}
                >
                  <DataCell>{weekLabel}</DataCell>
                  <DataCell sx={{ py: 1 }}>{renderWeekDays(week)}</DataCell>
                </DataRow>
              );
            })}

            {sortedWeeks.length === 0 && (
              <DataRow>
                <DataCell colSpan={2}>
                  <Text variant="body2">
                    {language?.eosDashboard?.noDataAvailable ?? 'No data.'}
                  </Text>
                </DataCell>
              </DataRow>
            )}
          </SimpleTable>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
}
